import moment from "moment";
import { shiftRequest } from "../ShiftEarnings/shiftEarningsApi";

// Payroll > Pay Inputs > Penalties talks to the Penalty APIs
// (TblPayrollDailyPenalty) under payroll/deduction/. The Shift Earnings
// request helper is reused (token, 401 / 409 handling, error text).
const BASE = "payroll/deduction/";

// API row -> list row. `penalty` is the quantity text ("0.5", "00:30", or the
// amount for amount basis); penalty_amount is what is deducted.
export const fromPenaltyApi = (row) => {
  const date = moment(row.date, ["DD-MM-YYYY", "YYYY-MM-DD"], true);
  return {
    ...row,
    date: date.isValid() ? date.format("YYYY-MM-DD") : row.date,
    quantity: row.penalty,
    payable_amount: row.penalty_amount,
    payroll_cycle: row.pay_schedule_name || "Unassigned",
    pay_schedule_id: row.pay_schedule,
  };
};

// Penalty Type options (GET payroll/deduction/penalty/types/). Accepts a plain
// list of names (["Late Entry", ...]) or objects with a name / label / value,
// the same as the Leave types call.
export const loadPenaltyTypes = async (domain, signal) => {
  const data = await shiftRequest(domain, `${BASE}penalty/types/`, { signal });
  const list = Array.isArray(data) ? data : data?.results ?? data?.data ?? data?.types;
  if (!Array.isArray(list)) throw new Error("The server returned an invalid penalty type list.");
  const name = (item) => (typeof item === "string" ? item : item?.name ?? item?.label ?? item?.penalty_type ?? item?.value ?? "");
  return [...new Set(list.map((item) => String(name(item)).trim()).filter(Boolean))];
};

export const toPenaltyPayload = (form, { creating = false } = {}) => {
  const amountBasis = form.basis === "amount";
  return {
    ...(creating ? { employee_id: form.employee_code } : {}),
    date: form.date,
    penalty_type: form.penalty_type,
    ...(creating && !form.shift_name ? {} : { shift: form.shift_name || null }),
    basis: form.basis,
    ...(amountBasis ? { amount: Number(form.amount) } : {
      quantity: String(form.quantity).trim(),
      rate: Number(form.rate),
      factor: Number(form.payment_factor),
    }),
    ...(form.pay_schedule_id === "" ? (creating ? {} : { pay_schedule_id: null }) : { pay_schedule_id: Number(form.pay_schedule_id) }),
    ...(!creating ? { status: form.status } : {}),
  };
};

export const listPenalties = async (domain, companyId, period, signal) => {
  const params = new URLSearchParams({ company_id: companyId, start_date: period.start, end_date: period.end });
  const data = await shiftRequest(domain, `${BASE}penalty_details/?${params}`, { signal });
  if (!Array.isArray(data)) throw new Error("The server returned an invalid penalties list.");
  return data.filter((row) => !row.isdeleted).map(fromPenaltyApi);
};

// Edits send only the fields that changed, so a status or payroll cycle
// change never resends (and recalculates) the amount.
export const savePenalty = async (domain, form, id, signal, initial) => {
  const payload = toPenaltyPayload(form, { creating: id == null });
  const before = initial ? toPenaltyPayload(initial) : null;
  const body = before ? Object.fromEntries(Object.entries(payload).filter(([key, value]) => value !== before[key])) : payload;
  const row = await shiftRequest(domain, `${BASE}penalty/${id == null ? "" : `${id}/`}`, {
    method: id == null ? "POST" : "PUT", body, signal,
  });
  if (row?.id == null) throw new Error("The server returned an invalid penalty.");
  return fromPenaltyApi(row);
};

export const deletePenalty = (domain, id, signal) => shiftRequest(domain, `${BASE}penalty/${id}/`, { method: "DELETE", signal });

export const bulkPenalties = async (domain, ids, changes, signal) => {
  const data = await shiftRequest(domain, `${BASE}penalty/bulk-action/`, { method: "PUT", body: { ids, ...changes }, signal });
  if (!Array.isArray(data?.updated) || !Array.isArray(data?.errors)) throw new Error("The server returned an invalid bulk action result. Refresh the list before trying again.");
  return data;
};

// Import and validate share one body: the records plus an optional payroll
// cycle for the whole file (blank = each row's own cycle). Both answer 400
// when no row passed, but still with the per-row results, so that body is
// returned instead of thrown.
const postImport = async (domain, path, records, paySchedule, signal, failText) => {
  const response = await fetch(`${domain.replace(/\/$/, "")}/${BASE}${path}`, {
    method: "POST",
    signal,
    headers: { "Content-Type": "application/json", Authorization: `Token ${localStorage.getItem("token")}` },
    body: JSON.stringify({ records, ...(paySchedule ? { pay_schedule_id: Number(paySchedule) } : {}) }),
  });
  if (response.status === 401) {
    localStorage.clear();
    window.location.href = "/auth/login";
    throw new Error("Your session has expired. Please sign in again.");
  }
  const data = await response.json().catch(() => null);
  if (Array.isArray(data?.rows)) return data;
  const message = data?.error || data?.detail || data?.message;
  throw new Error(typeof message === "string" && message ? message : `${failText} (${response.status}). Please try again.`);
};

// records: [{ employee_id, date, penalty_type, basis, quantity?, rate?, factor?, amount?, shift? }]
// Returns { total, success, failed, rows: [{ employee_id, date, status, message }] }.
export const importPenalties = (domain, records, paySchedule, signal) =>
  postImport(domain, "penalty/import/", records, paySchedule, signal, "Import failed");

// Checks every record on the server without saving. Returns { total, valid,
// failed, rows: [{ index, status: "valid" | "failed", message, amount,
// pay_schedule_name }] }, index = position in `records`.
export const validatePenaltyImport = (domain, records, paySchedule, signal) =>
  postImport(domain, "penalty/import/validate/", records, paySchedule, signal, "Validation failed");
